/**
 * Checkout — submit button lock + spinner state.
 *
 * Baymard UX rec: po kliknięciu CTA należy zablokować przycisk + pokazać visual
 * loading state. Bez tego user przy wolnym połączeniu klika 2-3x → multiple AJAX
 * submits → duplicate orders w bazie. Custom CTA (`hn-co__submit--mobile/desktop`)
 * nie ma natywnej obsługi WC `wc-checkout.js` blockUI (która działa tylko dla
 * default WC checkout form'a) — handlujemy jawnie.
 *
 * Flow:
 *  1. User klika Zamawiam → `submit` event na formie
 *  2. `checkout-field-validate.js` (capture phase) waliduje:
 *     - INVALID → `preventDefault` + `stopImmediatePropagation` → nasz bubble
 *       handler NIE odpala → button NIE blokuje się
 *     - VALID → bubble handler odpala → lock + isSubmitting=true
 *  3. WC AJAX (`wc-checkout.js`) startuje
 *     - SUCCESS → redirect → button zostaje locked (nie wraca, page navigates)
 *     - ERROR (server-side validation / gateway fail) → jQuery `checkout_error`
 *       trigger na `document.body` → unlock + isSubmitting=false
 *  4. Safety timeout 30s — jeśli WC AJAX zawiesi się bez sygnału, unlock
 *
 * Edge cases:
 *  - Enter w polu tekstowym też triggeruje form submit → `isSubmitting` flag
 *    blokuje re-entry przez `e.preventDefault()`
 *  - Disabled submit button nie odpala click → ale Enter w innym polu omija click,
 *    dlatego flag jest niezbędny
 */

const LOCK_TIMEOUT_MS = 30000;
const SUBMIT_BTN_SELECTOR = '[data-hn-co-submit]';

export function initCheckoutSubmitLock() {
    const form = document.querySelector('form.checkout.hn-co');
    if (!form) return;

    /* Submit buttons mogą zostać re-rendered przez WC `updated_checkout` (bardzo rzadko —
     * nasze CTA są poza `#order_review`), więc bierzemy z formy on-demand. */
    const getBtns = () => Array.from(form.querySelectorAll(SUBMIT_BTN_SELECTOR));

    let isSubmitting = false;
    let unlockTimer = null;

    const lock = () => {
        getBtns().forEach((btn) => {
            btn.disabled = true;
            btn.classList.add('is-loading');
            btn.setAttribute('aria-busy', 'true');
        });
        clearTimeout(unlockTimer);
        unlockTimer = setTimeout(() => {
            /* Safety: gdyby WC AJAX zawisł bez `checkout_error`, odblokowujemy
             * po 30s żeby user nie patrzył w wieczny spinner. */
            unlock();
        }, LOCK_TIMEOUT_MS);
    };

    const unlock = () => {
        isSubmitting = false;
        getBtns().forEach((btn) => {
            btn.disabled = false;
            btn.classList.remove('is-loading');
            btn.removeAttribute('aria-busy');
        });
        clearTimeout(unlockTimer);
        unlockTimer = null;
    };

    /* Lock na submit BUBBLE phase — capture-phase validate module już sprawdziło
     * formularz; jeśli był invalid, `stopImmediatePropagation` zapobiegło tej handlerce.
     * Czyli jeśli tu dotarliśmy, walidacja przeszła. */
    form.addEventListener('submit', (e) => {
        if (isSubmitting) {
            /* Re-entry guard (np. Enter w polu w trakcie pierwszego submitu). */
            e.preventDefault();
            return;
        }
        isSubmitting = true;
        lock();
    });

    /* Defensive: jeśli moduł walidacji dispatchnie 'hn-co-validation-failed' na formie
     * (po preventDefault), upewnij się że button NIE jest zablokowany. */
    form.addEventListener('hn-co-validation-failed', unlock);

    /* WC AJAX checkout returns error (server-side validation / gateway fail) — fires
     * `checkout_error` przez jQuery na document.body. Wymaga jQuery (WC dependency,
     * zawsze dostępne na checkout page). */
    if (window.jQuery) {
        window.jQuery(document.body).on('checkout_error', () => {
            unlock();
        });
    }
}
